import type { Tx } from '../../domain/tx';
import type { EntityMap, EntityName, Id } from '../../domain/types';
import { ENTITY_NAMES } from '../../domain/types';
import type { Tables } from '../types';
import { BaseStorage, emptyTables, type Change, type Executed } from './base';
import { applyFilter } from './query';

/** Indexed fields per object store (every store is keyed by `id`). */
export const INDEXES: Record<EntityName, readonly string[]> = {
  businesses: ['slug'],
  owners: ['businessId', 'phone'],
  staff: ['businessId'],
  services: ['businessId'],
  weeklyHours: ['businessId'],
  timeOff: ['businessId'],
  settings: ['businessId'],
  clients: ['businessId', 'phone'],
  appointments: ['businessId', 'start', 'clientId', 'manageToken'],
  waitlistEntries: ['businessId', 'clientId'],
  waitlistOffers: ['businessId', 'token', 'entryId', 'slotKey'],
  consents: ['businessId', 'clientId'],
  messages: ['businessId', 'appointmentId', 'scheduledFor', 'status'],
  payments: ['businessId', 'appointmentId'],
};

const DB_VERSION = 1;

const req = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });

function openDb(factory: IDBFactory, name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(name, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      for (const entity of ENTITY_NAMES) {
        const store = db.createObjectStore(entity, { keyPath: 'id' });
        for (const field of INDEXES[entity]) store.createIndex(field, field, { unique: false });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
    open.onblocked = () => reject(new Error('indexeddb open blocked'));
  });
}

/** Raw IndexedDB: one object store per entity; transactions are real IDB transactions (atomic across tabs). */
export class IndexedDbStorage extends BaseStorage {
  readonly kind = 'indexeddb' as const;
  private constructor(private readonly dbReady: Promise<IDBDatabase>, channelName?: string) {
    super(channelName);
  }

  /** Synchronous handle; the database opens in the background and every call waits for it. */
  static create(opts: { name?: string; factory?: IDBFactory; channelName?: string } = {}): IndexedDbStorage {
    const name = opts.name ?? 'planella';
    return new IndexedDbStorage(openDb(opts.factory ?? globalThis.indexedDB, name), opts.channelName ?? `planella-sync:${name}`);
  }

  /** Like create, but resolves once the database is open (surfaces open errors). */
  static async open(opts: { name?: string; factory?: IDBFactory; channelName?: string } = {}): Promise<IndexedDbStorage> {
    const storage = IndexedDbStorage.create(opts);
    await storage.dbReady;
    return storage;
  }

  private async select(
    store: IDBObjectStore,
    filter?: { where?: object; range?: { field: string; gte?: string | number; lt?: string | number } },
  ): Promise<unknown[]> {
    const indexed = (field: string): boolean => store.indexNames.contains(field);
    const range = filter?.range;
    if (range && indexed(range.field)) {
      const bound = range.gte !== undefined && range.lt !== undefined ? IDBKeyRange.bound(range.gte, range.lt, false, true)
        : range.gte !== undefined ? IDBKeyRange.lowerBound(range.gte)
        : range.lt !== undefined ? IDBKeyRange.upperBound(range.lt, true) : undefined;
      return req(store.index(range.field).getAll(bound));
    }
    const eq = Object.entries(filter?.where ?? {}).find(([k, v]) => indexed(k) && v !== undefined && v !== null && typeof v !== 'object');
    if (eq) return req(store.index(eq[0]).getAll(IDBKeyRange.only(eq[1] as IDBValidKey)));
    return req(store.getAll());
  }

  protected async execute<R>(entities: readonly EntityName[], mode: 'r' | 'rw', fn: (tx: Tx) => Promise<R>): Promise<Executed<R>> {
    const idb = (await this.dbReady).transaction([...entities], mode === 'rw' ? 'readwrite' : 'readonly');
    const changes: Change[] = [];
    const done = new Promise<void>((resolve, reject) => {
      idb.oncomplete = () => resolve();
      idb.onabort = () => reject(idb.error ?? new Error('transaction aborted'));
      idb.onerror = () => reject(idb.error);
    });
    const tx: Tx = {
      get: async (entity, id) => (await req(idb.objectStore(entity).get(id))) as never,
      list: async (entity, filter) => applyFilter((await this.select(idb.objectStore(entity), filter)) as EntityMap[typeof entity][], filter) as never,
      put: async (entity, row) => {
        await req(idb.objectStore(entity).put(row));
        changes.push({ entity, type: 'put', id: row.id });
        return row;
      },
      delete: async (entity, id) => {
        await req(idb.objectStore(entity).delete(id));
        changes.push({ entity, type: 'delete', id });
      },
    };
    let value: R;
    try {
      value = await fn(tx);
    } catch (err) {
      try { idb.abort(); } catch { /* already finished */ }
      await done.catch(() => undefined);
      throw err;
    }
    await done;
    return { value, changes };
  }

  protected async replaceAll(tables: Tables): Promise<void> {
    const idb = (await this.dbReady).transaction([...ENTITY_NAMES], 'readwrite');
    const done = new Promise<void>((resolve, reject) => { idb.oncomplete = () => resolve(); idb.onabort = () => reject(idb.error); });
    for (const entity of ENTITY_NAMES) {
      const store = idb.objectStore(entity);
      store.clear();
      for (const row of tables[entity] as Array<{ id: Id }>) store.put(row);
    }
    await done;
  }

  protected async dumpAll(): Promise<Tables> {
    const idb = (await this.dbReady).transaction([...ENTITY_NAMES], 'readonly');
    const out = emptyTables() as unknown as Record<EntityName, unknown[]>;
    for (const entity of ENTITY_NAMES) out[entity] = await req(idb.objectStore(entity).getAll());
    return out as unknown as Tables;
  }

  protected closeImpl(): void { void this.dbReady.then((db) => db.close(), () => undefined); }
}
